/**
 * Lap Mappers
 *
 * Bi-directional conversion between Lap domain model and LapDto
 */

import { Lap } from '../../domain/lap/Lap'
import { buildLap } from '../../domain/lap/buildLap'
import { LapDto, TelemetryDto } from '../apiTypes'
import { processRawTelemetry } from '../../processors/rawTelemetryProcessor'
import { telemetryDtoToRaw } from './telemetryMapper'
import { RawTelemetryData } from '../../domain/telemetry/raw/RawTelemetryData'

/**
 * Convert Lap domain model to LapDto for API calls
 *
 * This is used when saving lap data to the backend.
 *
 * Telemetry is optional. Pass it explicitly when the DTO must carry its own
 * telemetry (e.g. sharing a single lap). Omit it when the telemetry is
 * already being sent elsewhere in the request (e.g. saving a whole track
 * session, where per-lap telemetry would otherwise duplicate the
 * session-level telemetry payload for every lap).
 *
 * @param lap - The Lap domain model
 * @param telemetry - Optional telemetry DTO (convert from RawTelemetryData using rawTelemetryToDto)
 * @returns LapDto ready for API submission; `telemetry` is omitted when not provided
 */
export function lapToDto(lap: Lap, telemetry?: TelemetryDto): LapDto {
  return {
    id: lap.id,
    lapNumber: lap.lapNumber,
    startTime: lap.startTime,
    finishTime: lap.finishTime,
    lapTimeMs: lap.lapTime,
    trackSessionId: lap.sessionId,
    trackId: lap.trackId,
    weather: lap.weather,
    driverName: lap.driverName,
    ...(telemetry !== undefined ? { telemetry } : {}),
  }
}

function withCoordinateDerivedDistance(
  rawTelemetry: RawTelemetryData
): RawTelemetryData {
  return {
    ...rawTelemetry,
    gps: rawTelemetry.gps.map(
      ({ distanceMeters: _stored, ...reading }) => reading
    ),
  }
}

/**
 * Convert a LapDto (with telemetry) to a Lap domain model.
 *
 * The DTO's telemetry is processed like a one-lap session and the lap is
 * assembled by the same buildLap every other entry point uses.
 *
 * @param lapDto - The LapDto from the API (must include telemetry)
 * @returns Lap domain model ready for analysis, or null if telemetry is missing
 */
export function lapDtoToLap(lapDto: LapDto): Lap | null {
  if (!lapDto.telemetry) {
    return null
  }

  const startTime = new Date(lapDto.startTime)
  const rawTelemetry = withCoordinateDerivedDistance(
    telemetryDtoToRaw(lapDto.telemetry, startTime)
  )
  const sessionTelemetry = processRawTelemetry(
    rawTelemetry,
    rawTelemetry.recordingStartTime ?? startTime,
    typeof lapDto.telemetry.imuLagMs === 'number'
      ? { imuLagMs: lapDto.telemetry.imuLagMs }
      : {}
  )

  const lap = buildLap({
    metadata: {
      lapId: lapDto.id,
      lapNumber: lapDto.lapNumber,
      driverName: lapDto.driverName ?? 'Unknown',
      trackSessionId: lapDto.trackSessionId ?? '',
      trackId: lapDto.trackId,
    },
    window: { startTime, finishTime: new Date(lapDto.finishTime) },
    sessionTelemetry,
    sessionRawTelemetry: rawTelemetry,
    device: lapDto.telemetry.device,
    weather: lapDto.weather,
  })
  if (!lap) return null

  lap.isSelected = true
  lap.isShared = true
  return lap
}
